import { and, eq, gt, lt } from '@tanstack/db'
import { useLiveQuery } from '@tanstack/react-db'
import { useMemo } from 'react'
import { useStore } from '@/lib/pocketbase'
import { combineWithOr } from '@/lib/query-utils'
import type { UserGeofenceActivity } from '@/types/movement'
import type { Movement, Users } from '@/types/pbSchema'
import { useBatchUsers } from './useBatchUsers'

function buildActivityFromMovements(
    movements: Movement[],
    userMap: Map<string, Users>
): UserGeofenceActivity[] {
    const userActivity = new Map<string, UserGeofenceActivity>()
    for (const movement of movements) {
        if (!movement.user || !movement.geofence_type) continue
        if (userActivity.has(movement.user)) continue

        const userName = userMap.get(movement.user)?.name ?? 'Unknown User'
        userActivity.set(movement.user, {
            userId: movement.user,
            userName,
            enterTime: movement.geofence_type === 'ENTER' ? movement.timestamp : null,
            exitTime: movement.geofence_type === 'EXIT' ? movement.timestamp : null,
        })
    }
    return Array.from(userActivity.values())
}

interface UseGeofenceActivityParams {
    orgUserIds: string[]
    startDate: string
    endDate: string
    selectedLocationId: string | null
}

export function useGeofenceActivity({
    orgUserIds,
    startDate,
    endDate,
    selectedLocationId,
}: UseGeofenceActivityParams) {
    const [movementCollection] = useStore('movement')

    const { data: geofenceMovements = [], isLoading: isLoadingGeofence } = useLiveQuery(
        q => {
            if (!selectedLocationId || orgUserIds.length === 0) return undefined

            return q
                .from({ movement: movementCollection })
                .where(({ movement }) =>
                    and(
                        eq(movement.geofence_location, selectedLocationId),
                        combineWithOr(orgUserIds.map(id => eq(movement.user, id))),
                        gt(movement.timestamp, startDate),
                        lt(movement.timestamp, endDate)
                    )
                )
                .orderBy(({ movement }) => movement.timestamp, 'desc')
                .limit(orgUserIds.length * 5)
        },
        [selectedLocationId, orgUserIds, startDate, endDate]
    )

    const geofenceUserIds = useMemo(() => {
        return Array.from(new Set(geofenceMovements.map(m => m.user).filter(Boolean) as string[]))
    }, [geofenceMovements])

    const { userMap: geofenceUserMap, isPending: isLoadingGeofenceUsers } =
        useBatchUsers(geofenceUserIds)

    const selectedLocationActivity = useMemo(() => {
        if (!selectedLocationId) return []
        return buildActivityFromMovements(geofenceMovements, geofenceUserMap)
    }, [selectedLocationId, geofenceMovements, geofenceUserMap])

    return {
        selectedLocationActivity,
        isPending: isLoadingGeofence || (geofenceUserIds.length > 0 && isLoadingGeofenceUsers),
    }
}
